import type { Theme } from "@atlassian/wac-common-constants/src/theme-preference";
import type AnalyticsClient from "@atlassiansox/analytics-web-client";
import type {
    OperationalEventPayload,
    ScreenEventPayload,
    TrackEventPayload,
    UIEventPayload
} from "@atlassiansox/analytics-web-client";

export type Attributes = {
    [key: string]: boolean | string | number | undefined | null | Attributes;
};

export type UIViewedAttributes = {
    theme?: Theme;
    OStheme?: Theme;
};

export type OriginTracingHandlerResponse = {
    originTracingAttributes: Attributes;
    taskSessionId?: string;
};

export type OriginTracingHandler = (
    encodedOrigin: string
) => OriginTracingHandlerResponse;

export type OriginTracingHandlerMapping = {
    [param: string]: OriginTracingHandler;
};

export const ANALYTICS_EVENT_TYPES = [
    "SCREEN",
    "UI",
    "TRACK",
    "OPERATIONAL"
] as const;
export type AnalyticsEventType = (typeof ANALYTICS_EVENT_TYPES)[number];

export const isAnalyticsEvent = (value?: unknown): value is AnalyticsEvent =>
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    typeof value.type === "string" &&
    ANALYTICS_EVENT_TYPES.includes(value.type as AnalyticsEventType);

interface AnalyticsEventObject<Type, Payload> {
    type: Type;
    payload: Payload;
}

export type ScreenEvent = AnalyticsEventObject<"SCREEN", ScreenEventPayload>;
export type UIEvent = AnalyticsEventObject<"UI", UIEventPayload>;
export type TrackEvent = AnalyticsEventObject<"TRACK", TrackEventPayload>;
export type OperationalEvent = AnalyticsEventObject<
    "OPERATIONAL",
    OperationalEventPayload
>;
export type UnknownEvent = AnalyticsEventObject<unknown, unknown>;

export type AnalyticsEvent =
    | ScreenEvent
    | UIEvent
    | TrackEvent
    | OperationalEvent;

export interface AnalyticsWebClient {
    init(): boolean;
    sendEvent(event: AnalyticsEvent | UnknownEvent): void;
    getInstance(): AnalyticsClient;
}
